import { RhInput } from "@rhythm-ui/react";
import React, { useMemo } from "react";

import FilterHeader from "./FilterHeader";

type GenericType<T> = {
  label: string;
  subLabel?: string;
  filterOn: T;
  setFilterOn: React.Dispatch<React.SetStateAction<T>>;
  minKey: keyof T;
  maxKey: keyof T;
  handleClearFilterKey: (key: (keyof T)[]) => void;
  minValueToBeGreaterThan?: number;
  __onInputChange?: () => void;
};

const MinMaxFilterInput = <T extends object>({
  label,
  subLabel,
  filterOn,
  setFilterOn,
  minKey,
  maxKey,
  handleClearFilterKey,
  minValueToBeGreaterThan,
  __onInputChange,
}: GenericType<T>) => {
  const onFilterInputChangeHandler = (key: keyof T, value: number) => {
    if (isNaN(value)) {
      setFilterOn((state) => {
        const { ...rest } = state;
        delete rest[key];
        return rest;
      });
    } else {
      setFilterOn({
        ...filterOn,
        [key]: value,
      });
    }
  };

  const onMinChange = (value: number) => {
    if (
      (typeof minValueToBeGreaterThan !== "undefined" &&
        value <= minValueToBeGreaterThan) ||
      value < 0
    )
      return;

    onFilterInputChangeHandler(minKey, value);
    __onInputChange ? __onInputChange() : null;
  };

  const onMaxChange = (value: number) => {
    if (value <= 0) return;
    onFilterInputChangeHandler(maxKey, value);
    __onInputChange ? __onInputChange() : null;
  };

  let min: T[keyof T] | undefined = filterOn[minKey];

  if (typeof min !== "number") min = undefined;
  const maxShouldBeGreaterThan = typeof min === "string" ? undefined : min;

  let max: T[keyof T] | undefined = filterOn[maxKey];
  if (typeof max !== "number") max = undefined;
  const minShouldBeLessThan = typeof max === "string" ? undefined : max;

  const isFieldError = useMemo(() => {
    if (!min || !max) return false;
    return min >= max;
  }, [min, max]);

  const onMaxBlurHandler = () => {
    if (isFieldError) {
      setFilterOn((state) => {
        const { ...rest } = state;
        delete rest[maxKey];
        return rest;
      });
    }
  };

  return (
    <>
      <FilterHeader
        title={label}
        subTitle={subLabel}
        handleClearFilterKey={handleClearFilterKey}
        values={filterOn}
        valueKey={[minKey, maxKey]}
      />
      <div className="relative flex items-center justify-around gap-tsm mt-tpico mb-tsm">
        <div className="flex-1">
          <p className="!mb-tpico body-small text-hint">Min</p>
          <RhInput
            type="number"
            inputSize="sm"
            className="w-full"
            value={min ?? ""}
            min={0}
            max={minShouldBeLessThan ? minShouldBeLessThan - 1 : ""}
            onChange={(e) => onMinChange(parseInt(e.target.value))}
            placeholder="Any"
            onWheel={(e: any) => e.target.blur()}
            error={isFieldError}
          />
        </div>

        <div className="flex-1">
          <p className="body-small text-hint !mb-tpico">Max</p>
          <RhInput
            type="number"
            inputSize="sm"
            className="w-full"
            value={max ?? ""}
            min={maxShouldBeGreaterThan ? maxShouldBeGreaterThan + 1 : 1}
            max={50}
            onChange={(e) => onMaxChange(parseInt(e.target.value))}
            placeholder="Any"
            onWheel={(e: any) => e.target.blur()}
            error={isFieldError}
            onBlur={onMaxBlurHandler}
          />
        </div>
        {isFieldError && (
          <span className="absolute left-0 text-xs text-danger-500 top-full">
            Max cannot be less or equal to min
          </span>
        )}
      </div>
    </>
  );
};
export default MinMaxFilterInput;
